import {useMemo, useState} from 'react'
import {useDebounce} from '@github-ui/use-debounce'
import {useAvailableReviewers} from '../../../../page-data/loaders/use-available-reviewers'
import {legacyReviewerToProto} from '../../../../page-data/payloads/review-requests'

/**
 * Owns the picker's filter state and the debounced server-side typeahead query.
 * The raw `filter` drives the input; `debouncedFilter` is pushed to the endpoint
 * on a 200ms debounce so it isn't hit on every keystroke. The hook is mounted
 * only while the panel is open, so each open starts from an empty filter.
 */
export function useReviewersDebounced() {
  const [filter, setFilter] = useState('')
  const [debouncedFilter, setDebouncedFilter] = useState('')

  const updateDebouncedFilter = useDebounce((next: string) => setDebouncedFilter(next), 200, {
    onChangeBehavior: 'cancel',
  })

  const onFilterChange = (next: string) => {
    setFilter(next)
    updateDebouncedFilter(next.trim())
  }

  const {data, isFetching} = useAvailableReviewers({query: debouncedFilter, enabled: true})

  // available_reviewers is not yet on proto; adapt its legacy reviewers up to the
  // proto Reviewer shape the picker consumes. Memoized so an unchanged response
  // keeps a stable array identity.
  const searchedReviewers = useMemo(() => data?.availableReviewers.map(legacyReviewerToProto), [data])

  return {
    filter,
    onFilterChange,
    searchedReviewers,
    isFetching: isFetching || filter.trim() !== debouncedFilter,
  }
}
